import type { ErrorMode } from './types';
import { useEffect, useRef } from 'react';
import { FieldValues, useFormContext, UseFormReturn } from 'react-hook-form';

type FieldValuesChangeHandler<TFieldValues extends FieldValues> = (fieldValues: TFieldValues) => void;

export const useFormValuesWatcher = <TFieldValues extends FieldValues>(
  form: UseFormReturn<TFieldValues>,
  onFieldValuesChange: FieldValuesChangeHandler<TFieldValues>
) => {
  const { watch } = form;

  const handleFieldValuesChangeRef = useRef<FieldValuesChangeHandler<TFieldValues>>(onFieldValuesChange);
  handleFieldValuesChangeRef.current = onFieldValuesChange;

  // Watch form values and update the redux store when they change
  useEffect(() => {
    const subscription = watch((fieldValues) => {
      handleFieldValuesChangeRef.current && handleFieldValuesChangeRef.current(fieldValues as TFieldValues);
    });

    return () => subscription.unsubscribe();
  }, [watch]);
};

export const useFieldErrorMessage = (fieldName: string, errorMode?: ErrorMode) => {
  const { getFieldState, formState } = useFormContext();

  const fieldState = getFieldState(fieldName, formState);
  if (!fieldState.error) return undefined;

  const showError =
    formState.submitCount > 0 || (errorMode === 'immediate' && (fieldState.isTouched || fieldState.isDirty));

  return showError ? fieldState.error.message : undefined;
};
